import { Avatar, Tooltip } from 'antd';
import PropTypes from 'prop-types';

import { PROPOSAL_STATUS } from 'constants/index';
import { getProposalTitle, getClientName, getSeenTimeDetails } from 'helpers/proposal';
import { getUserShortName } from 'helpers/users';
import ProposalItem from './ProposalItem';

/**
 * Proposal item renderer
 * @param {object} proposal - Proposal
 * @param {number} proposalIndex - Proposal index
 * @param {boolean} trash - Trash
 * @param {string} showMonthName - Show month name
 * @param {object} user - User
 * @param {array} teamMembers - Team members
 * @param {array} proposals - Proposals
 * @param {number} tagWrapperWidth - Tag wrapper width
 * @param {function} setTagWrapperWidth - Set tag wrapper width
 * @param {function} onClickItem - On click item
 * @param {array} tagsData - Tags data
 * @param {function} setTagsData - Set tags data
 * @param {object} proposalsVar - Proposals variable
 * @param {function} updateProposalTags - Update proposal tags
 * @param {function} setIgnoreUpdates - Set ignore updates
 * @param {array} tagsFilter - Tags filter
 * @param {object} lockLoadingMap - Lock loading map
 * @param {object} loadingMap - Loading map
 * @param {function} handleSignedSeen - Handle signed seen
 * @param {boolean} showSelectionCheckboxes - Show selection checkboxes
 * @param {function} isProposalSelected - Is proposal selected
 * @param {function} toggleProposalSelection - Toggle proposal selection
 */
const ProposalItemRenderer = ({
  proposal,
  proposalIndex,
  trash,
  showMonthName,
  user,
  teamMembers,
  proposals,
  tagWrapperWidth,
  setTagWrapperWidth,
  onClickItem,
  tagsData,
  setTagsData,
  proposalsVar,
  updateProposalTags,
  setIgnoreUpdates,
  tagsFilter,
  lockLoadingMap,
  loadingMap,
  handleSignedSeen,
  showSelectionCheckboxes,
  isProposalSelected,
  toggleProposalSelection,
}) => {
  const { pid: proposalId, state = 'edit' } = proposal;

  if (proposalId && proposalId.length < 36) {
    return null;
  }

  const proposalStatus = PROPOSAL_STATUS[state];
  const proposalTitle = getProposalTitle({ proposal, user });
  const clientName = getClientName({ proposal });

  let { minPrice, maxPrice, hasNegativePrice } = proposal?.quotes || {
    minPrice: '$0',
    maxPrice: '$0',
  };

  const seenTime = getSeenTimeDetails({ proposal });
  const { seenCount, openLogs } = proposal;

  let opened = seenCount || openLogs?.length;
  opened =
    opened && proposalStatus !== 'DRAFT' && new Date(proposal.date) > new Date(2019, 5, 30)
      ? opened
      : null;

  const proposalUser =
    proposal?.uid === user?._id
      ? user
      : teamMembers?.length
      ? teamMembers?.find((teamMember) => teamMember._id === proposal.uid)
      : null;

  let avatar = '';

  if (proposalUser) {
    avatar = (
      <Tooltip title={proposalUser?.profile?.name}>
        <Avatar src={proposalUser?.profile?.image} size={40}>
          {getUserShortName({ name: proposalUser?.profile?.name })}
        </Avatar>
      </Tooltip>
    );
  }

  const teamInfo = proposalUser
    ? {
        userId: user?._id,
        userRole: user?.roles && user?.roles[0],
        uid: proposal.uid,
        auid: proposal.auid,
      }
    : null;

  const watched =
    seenTime && proposalStatus !== 'DRAFT' && new Date(proposal.date) > new Date(2019, 5, 30)
      ? `${seenTime.minutes}:${seenTime.seconds} min`
      : '';

  const quote =
    minPrice === maxPrice ? `${minPrice || '$0'}` : `${minPrice || '$0'} - ${maxPrice || '$0'}`;

  const isDeletable =
    user?._id === proposal?.auid || //user is admin
    proposal.isOwner || //user is owner
    (user?.roles?.includes('manager') && !proposal.isManager); // user is manager and proposal is of editor

  /**
   * Render proposal item renderer
   * @returns
   */
  return (
    <div>
      {showMonthName && <div className="month-name">{showMonthName}</div>}

      <ProposalItem
        proposalIndex={proposalIndex}
        tagWrapperWidth={tagWrapperWidth}
        setTagWrapperWidth={setTagWrapperWidth}
        teamInfo={teamInfo}
        pnum={proposal.pnum}
        trash={trash}
        tags={proposal.tags}
        name={proposalTitle}
        client={clientName}
        avatar={avatar}
        watched={watched}
        opened={opened}
        emailLogs={proposal.emailLogs}
        lastSeen={proposal.lastSeen}
        clientIP={proposal?.signature?.clientIP}
        isSecured={proposal?.isSecured}
        quote={quote}
        hasNegativePrice={hasNegativePrice}
        status={proposalStatus || 'DRAFT'}
        prevState={PROPOSAL_STATUS[proposal.prevState]}
        onClickItem={onClickItem}
        isScheduled={proposal?.isScheduled}
        scheduleDate={proposal?.emailSettings?.scheduled?.scheduleTime}
        proposalId={proposalId}
        isLocked={user?.paymentStatus?.canLock}
        variables={proposal?.draft?.variables}
        isDeletable={isDeletable}
        auid={user?.teamId || user._id}
        pid={proposal._id}
        tagsData={tagsData}
        setTagsData={setTagsData}
        proposalsVar={proposalsVar}
        proposalList={proposals}
        updateProposalTags={updateProposalTags}
        setIgnoreUpdates={setIgnoreUpdates}
        tagsFilter={tagsFilter}
        language={proposal?.language}
        lastStatusUpdateTime={proposal?.lastStatusUpdateTime}
        lockedData={proposal?.lockedData}
        lockLoadingMap={lockLoadingMap}
        loadingMap={loadingMap}
        handleSignedSeen={handleSignedSeen}
        isSelected={showSelectionCheckboxes ? isProposalSelected?.(proposalId) : false}
        onToggleSelect={showSelectionCheckboxes ? toggleProposalSelection : undefined}
      />
    </div>
  );
};

/**
 * Proposal item renderer default props
 */
ProposalItemRenderer.defaultProps = {
  proposal: null,
  proposalIndex: 0,
  trash: false,
  showMonthName: '',
  user: null,
  teamMembers: [],
  proposals: [],
  tagWrapperWidth: 0,
  setTagWrapperWidth: () => {},
  onClickItem: () => {},
  tagsData: [],
  setTagsData: () => {},
  proposalsVar: null,
  updateProposalTags: () => {},
  setIgnoreUpdates: () => {},
  tagsFilter: [],
  lockLoadingMap: {},
  loadingMap: {},
  handleSignedSeen: () => {},
  showSelectionCheckboxes: false,
  isProposalSelected: () => false,
  toggleProposalSelection: () => {},
};

/**
 * Proposal item renderer prop types
 */
ProposalItemRenderer.propTypes = {
  proposal: PropTypes.object.isRequired,
  proposalIndex: PropTypes.number.isRequired,
  trash: PropTypes.bool.isRequired,
  showMonthName: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  teamMembers: PropTypes.array.isRequired,
  proposals: PropTypes.array.isRequired,
  tagWrapperWidth: PropTypes.number.isRequired,
  setTagWrapperWidth: PropTypes.func.isRequired,
  onClickItem: PropTypes.func.isRequired,
  tagsData: PropTypes.array.isRequired,
  setTagsData: PropTypes.func.isRequired,
  proposalsVar: PropTypes.object.isRequired,
  updateProposalTags: PropTypes.func.isRequired,
  setIgnoreUpdates: PropTypes.func.isRequired,
  tagsFilter: PropTypes.array.isRequired,
  lockLoadingMap: PropTypes.object.isRequired,
  loadingMap: PropTypes.object.isRequired,
  handleSignedSeen: PropTypes.func.isRequired,
  showSelectionCheckboxes: PropTypes.bool.isRequired,
  isProposalSelected: PropTypes.func.isRequired,
  toggleProposalSelection: PropTypes.func.isRequired,
};

/**
 * Proposal item renderer
 */
export default ProposalItemRenderer;
